import { isEqual } from "lodash";
import { useState, useMemo, useCallback, useEffect } from "react";

import { isAuthorized } from "src/custom-hooks/useAuthorization/useAuthorization";
import { EmployeesApiService } from "src/services/employees/employees-api.service";
import { getLocalStorageValue } from "src/utils/local-storage-utils";
import { EmployeeQuickFilter } from "./interfaces";

import { useAppSelector } from "../../../custom-hooks/redux/redux";
import { EmployeeRef, EmployeesFilter } from "../../../models/employees";

export const useEmployeesQuickFilter = (
  allEmployeesAsDefault = false,
  employeeGroupIds?: string[],
  legalEntityIds?: string[],
  shouldLoad = true,
  useLocalStorage = false,
  lsIdentifier?: string,
  authorizations?: string[],
  optionIsAuthorizedChecker?: (...params: any) => boolean,
  optionIncluded?: (emp: EmployeeRef) => boolean
) => {
  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  const [employees, setCurrEmployees] = useState<EmployeeRef[]>([]);

  const [quickfilterIsLoading, setQuickfilterIsLoading] = useState(true);

  const loadItemCallback = useCallback(
    async (signal: AbortSignal) => {
      setQuickfilterIsLoading(true);
      try {
        const response = await new EmployeesApiService().loadItems(
          {
            employeeGroupIds: employeeGroupIds ?? [],
            legalEntityIds: legalEntityIds ?? [],
          } as Partial<EmployeesFilter>,
          undefined,
          undefined,
          { pageNumber: 0, pageSize: 1000 },
          undefined,
          60,
          signal
        );
        if (!response.error) {
          const optionsIncluded = optionIncluded ? response.items.filter((emp) => optionIncluded(emp)) : response.items;

          const authorizedEmployees = authorizations
            ? optionIsAuthorizedChecker
              ? optionsIncluded.filter((emp) =>
                  optionIsAuthorizedChecker([...authorizations], currentUserAuthorizations, [emp.id])
                )
              : isAuthorized([...authorizations], currentUserAuthorizations)
              ? optionsIncluded
              : []
            : optionsIncluded;

          setCurrEmployees(authorizedEmployees as EmployeeRef[]);

          setQuickFilter({
            employees:
              optionIncluded && allEmployeesAsDefault
                ? {
                    withoutEmployee: false,
                    employeeIds: optionsIncluded.map((emp) => emp.id),
                  }
                : {
                    ...(useLocalStorage && getLocalStorageValue(lsIdentifier ?? "QF_EMP")?.employeeIds.length > 0
                      ? {
                          ...getLocalStorageValue(lsIdentifier ?? "QF_EMP"),
                        }
                      : {
                          withoutEmployee: false,
                          employeeIds: allEmployeesAsDefault
                            ? optionIncluded
                              ? optionsIncluded.map((emp) => emp.id)
                              : []
                            : [(authorizedEmployees[0] as EmployeeRef)?.id ?? []],
                        }),
                  },
          });
        }
      } catch (error: any) {
        console.error(error);
      } finally {
        setQuickfilterIsLoading(false);
      }
    },
    [
      authorizations,
      optionIsAuthorizedChecker,
      optionIncluded,
      allEmployeesAsDefault,
      currentUserAuthorizations,
      employeeGroupIds,
      legalEntityIds,
      lsIdentifier,
      useLocalStorage,
    ]
  );

  useEffect(() => {
    const abortController = new AbortController();

    if (shouldLoad) {
      loadItemCallback(abortController.signal);
    }

    return () => {
      abortController.abort();
    };
  }, [loadItemCallback, shouldLoad]);

  const [quickFilter, setQuickFilter] = useState<EmployeeQuickFilter>({
    employees: {
      ...(useLocalStorage && getLocalStorageValue(lsIdentifier ?? "QF_EMP")?.employeeIds.length > 0
        ? {
            ...getLocalStorageValue(lsIdentifier ?? "QF_EMP"),
          }
        : {
            withoutEmployee: false,
            employeeIds: allEmployeesAsDefault
              ? optionIncluded
                ? employees.filter((emp) => optionIncluded(emp)).map((emp) => emp.id)
                : []
              : [
                  ...(employees[0]?.id
                    ? [employees[0].id]
                    : optionIncluded
                    ? employees.filter((emp) => optionIncluded(emp)).map((emp) => emp.id)
                    : []),
                ],
          }),
    },
  });

  const selectedEmployeeId = useMemo(() => quickFilter.employees.employeeIds[0], [quickFilter.employees.employeeIds]);

  const selectedEmployee = useMemo(
    () => employees.filter((emp) => emp.id === selectedEmployeeId)[0] ?? null,
    [employees, selectedEmployeeId]
  );

  const handleChangeQuickFilter = useCallback(
    (filter: keyof EmployeeQuickFilter, value: EmployeeQuickFilter["employees"], allItems?: EmployeeRef[]) => {
      if (isEqual(value, quickFilter[filter])) return;

      if (!isEqual(allItems, employees) && !!allItems) setCurrEmployees(allItems);

      setQuickFilter((state) => ({ ...state, [filter]: value }));

      if (useLocalStorage) {
        localStorage.setItem(lsIdentifier ?? "QF_EMP", JSON.stringify(value));
      }
    },
    [quickFilter, lsIdentifier, useLocalStorage, employees]
  );

  return {
    employeesQuickFilter: quickFilter,
    setEmployeesQuickFilter: handleChangeQuickFilter,
    selectedEmployee,
    employeeQFIsLoading: quickfilterIsLoading,
    allEmployees: employees,
  };
};
